import { Link } from 'react-router-dom';

import { format2 } from 'utils/number';
import { capitalizeFirstLetter } from 'utils/strings';
import { formatLeaseAndWell } from 'utils/well-name';
import { wellStatusLabel } from 'utils/well-status';
import type { Eval3ScopedPopupValue } from './types';
import { getAreaHref } from './utils';

/**
 * Joins the present parts of a label with a middot separator.
 *
 * @param {(string | null)[]} parts - The candidate label parts.
 * @return {string | null} The joined label, or null when every part is absent.
 */
const joinLabel = (parts: (string | null)[]): string | null => {
  const present = parts.filter((part): part is string => Boolean(part));
  return present.length > 0 ? present.join(' · ') : null;
};

/**
 * The CRM edit page for a Salesforce tract/subtract id, as the Assets table links it.
 *
 * @param {string} tractId - The Salesforce tract id.
 * @return {string} The tract's CRM Asset edit path.
 */
const tractHref = (tractId: string) =>
  `/crm/tracts_and_subtracts/${encodeURIComponent(tractId)}/edit`;

/**
 * The inner content of the Eval 3 map's Scoped hover panel (Slots, Tracts, Units, LTD
 * Slots) — the General layers already pop up via the shared dispatcher, and this
 * closes the gap for the four scoped layers (INS-14081). Lists each hovered item's
 * identity from its tile properties, grouped by kind; a slot's UWI links to its well
 * page, a tract to its CRM Asset page, and a unit to its Development Area — each in a
 * new tab, matching the General well popups. Rendered inside `MapPopup`'s shared
 * `<Popup>` wrapper (same as the other content popups). Empty groups render nothing.
 *
 * @param {object} props - The grouped, deduped features under the cursor.
 * @param {Eval3ScopedPopupValue} props.value - The payload from `extractEval3ScopedFeatures`.
 * @return {JSX.Element} The grouped identity rows.
 */
export function Eval3ScopedContent({
  value,
}: {
  value: Eval3ScopedPopupValue;
}) {
  return (
    <div>
      {value.slots.length > 0 && (
        <section>
          <strong>{value.slots.length > 1 ? 'Slots' : 'Slot'}</strong>
          {value.slots.map((slot) => {
            // Lease + well number as the well popups format it ("GUNSMOKE #1H"),
            // and the status humanized the same way (`wellStatusLabel`) so the
            // panel reads "Shut In", not the stored `shut_in`.
            const name = formatLeaseAndWell(slot.leaseName, slot.wellNumber);
            return (
              <div key={slot.uwi}>
                <Link
                  to={`/wells/${slot.uwi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {slot.uwi}
                </Link>
                {name && <p>{name}</p>}
                {slot.status && <p>{wellStatusLabel(slot.status)}</p>}
              </div>
            );
          })}
        </section>
      )}

      {value.tracts.length > 0 && (
        <section>
          <strong>{value.tracts.length > 1 ? 'Tracts' : 'Tract'}</strong>
          {value.tracts.map((tract) => (
            // A Tract held in part lists several rows under one tractId (each Subtract's
            // shape, and the outline), told apart by subtractId (INS-14081).
            <div key={`${tract.tractId}\u0000${tract.subtractId ?? ''}`}>
              <Link
                to={tractHref(tract.tractId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {tract.name ?? tract.tractId}
              </Link>
              {tract.netAcreage !== null && (
                <p>
                  {format2(tract.netAcreage)}
                  {tract.netAcreageUnits ? ` ${tract.netAcreageUnits}` : ''}
                </p>
              )}
            </div>
          ))}
        </section>
      )}

      {value.units.length > 0 && (
        <section>
          <strong>{value.units.length > 1 ? 'Units' : 'Unit'}</strong>
          {value.units.map((unit) => (
            <div key={unit.name}>
              <Link
                to={getAreaHref(unit.name)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {unit.name}
              </Link>
              {unit.unitType && <p>{capitalizeFirstLetter(unit.unitType)}</p>}
            </div>
          ))}
        </section>
      )}

      {value.ltdSlots.length > 0 && (
        <section>
          <strong>
            {value.ltdSlots.length > 1 ? 'LTD Slots' : 'LTD Slot'}
          </strong>
          {value.ltdSlots.map((ltd) => {
            const label =
              joinLabel([
                ltd.unitName,
                ltd.formationName,
                ltd.slotNumber !== null ? `Slot ${ltd.slotNumber}` : null,
              ]) ?? ltd.slotId;
            return <p key={ltd.slotId}>{label}</p>;
          })}
        </section>
      )}
    </div>
  );
}
